/**
 * Product Analytics System
 * Analyzes individual products against their category for content creation
 */

export interface ProductAnalysis {
  score: number
  price: {
    current: number
    historical30d: number | null
    allTimeLowest: number | null
    vsCategory: number // Percentage difference from category average
    savingsOpportunity: number
    isPriceDrop: boolean
    dealQuality: 'excellent' | 'good' | 'fair' | 'none'
  }
  competitive: {
    priceTier: 'budget' | 'mid-range' | 'premium' | 'luxury'
    ratingVsAvg: number
    capacityVsAvg: number
    uniqueSellingPoints: string[]
    targetCustomer: string
  }
  features: {
    hasSmartFeatures: boolean
    hasIceMaker: boolean
    isEnergyStarCertified: boolean
    isCounterDepth: boolean
    doorConfig: number | null
    doorInDoor: boolean
    specialFeatures: string[]
  }
  sizing: {
    fitsStandardSpace: boolean
    requiresCustomSpace: boolean
    capacityPerSquareInch: number
    spaceEfficiencyScore: number
  }
  reviews: {
    overallSentiment: 'excellent' | 'good' | 'mixed' | 'poor'
    trustScore: number
    isHighlyRated: boolean
    socialProof: string
  }
}

// Helper to infer specs from text (title/description)
function inferSpecsFromText(text: string) {
  const t = text.toLowerCase()
  
  // Wine/Beverage specific
  const bottleCapacity = t.match(/(\d+)\s*-?\s*bottle/i)?.[1] ? parseInt(t.match(/(\d+)\s*-?\s*bottle/i)![1]) : null
  const canCapacity = t.match(/(\d+)\s*-?\s*can/i)?.[1] ? parseInt(t.match(/(\d+)\s*-?\s*can/i)![1]) : null
  
  // Commercial/Kegerator specific
  const taps = t.match(/(\d+)\s*-?\s*tap/i)?.[1] ? parseInt(t.match(/(\d+)\s*-?\s*tap/i)![1]) : (t.includes('single tap') ? 1 : t.includes('dual tap') ? 2 : null)
  const kegs = t.match(/(\d+)\s*-?\s*keg/i)?.[1] ? parseInt(t.match(/(\d+)\s*-?\s*keg/i)![1]) : null
  
  return {
    capacity: t.match(/(\d+\.?\d*)\s*cu\.?\s*ft/i)?.[1] ? parseFloat(t.match(/(\d+\.?\d*)\s*cu\.?\s*ft/i)![1]) : null,
    counterDepth: t.includes('counter depth') || t.includes('counter-depth'),
    smart: t.includes('smart') || t.includes('wifi') || t.includes('wi-fi') || t.includes('app control'),
    iceMaker: t.includes('ice maker') || t.includes('icemaker'),
    waterDispenser: t.includes('water dispenser') || t.includes('ice and water'),
    fingerprint: t.includes('fingerprint resistant') || t.includes('printproof'),
    stainless: t.includes('stainless steel'),
    doorInDoor: t.includes('door-in-door') || t.includes('door in door'),
    convertible: t.includes('convertible') || t.includes('flex zone') || t.includes('flexzone'),
    // New fields
    dualZone: t.includes('dual zone') || t.includes('dual-zone'),
    builtIn: t.includes('built-in') || t.includes('undercounter'),
    lock: t.includes('lock') || t.includes('security'),
    glassDoor: t.includes('glass door'),
    commercial: t.includes('commercial') || t.includes('nsf') || t.includes('etl'),
    bottleCapacity,
    canCapacity,
    taps,
    kegs
  }
}

export function analyzeProduct(
  product: any,
  similarProducts: any[] = []
): ProductAnalysis {
  // Infer missing specs from title
  const inferred = inferSpecsFromText(product.title || '')
  
  // Merge explicit specs with inferred specs
  const specs = {
    ...product.specs,
    ...product.specs_json,
    smart_features: product.specs_json?.smart_features || inferred.smart,
    ice_maker: product.specs_json?.ice_maker || inferred.iceMaker,
    water_dispenser: product.specs_json?.water_dispenser || inferred.waterDispenser,
    energy_star: product.specs_json?.energy_star || (product.title?.toLowerCase().includes('energy star')),
    installation: product.specs_json?.installation || (inferred.counterDepth ? 'counter-depth' : undefined)
  }
  
  const capacity = product.capacity_cuft || product.capacity || inferred.capacity || 20
  
  // Price Analysis
  const prices = similarProducts.map(p => p.price).filter((p): p is number => typeof p === 'number' && p > 0)
  const avgCategoryPrice = prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : (product.price > 0 ? product.price : 0)
  
  // Avoid division by zero or NaN
  let vsCategory = 0
  if (avgCategoryPrice > 0 && product.price > 0) {
    vsCategory = ((product.price - avgCategoryPrice) / avgCategoryPrice) * 100
  } else {
    vsCategory = 0 // Default if no comparison possible
  }
  
  const isPriceDrop = product.originalPrice && product.originalPrice > product.price
  const savingsOpportunity = isPriceDrop ? product.originalPrice - product.price : 0
  
  // Determine price tier
  let priceTier: 'budget' | 'mid-range' | 'premium' | 'luxury' = 'mid-range'
  if (product.price < avgCategoryPrice * 0.7) priceTier = 'budget'
  else if (product.price > avgCategoryPrice * 1.5) priceTier = 'luxury'
  else if (product.price > avgCategoryPrice * 1.2) priceTier = 'premium'
  
  // Deal quality scoring
  let dealQuality: 'excellent' | 'good' | 'fair' | 'none' = 'none'
  if (isPriceDrop) {
    const dropPercent = (savingsOpportunity / product.originalPrice) * 100
    if (dropPercent >= 20) dealQuality = 'excellent'
    else if (dropPercent >= 10) dealQuality = 'good'
    else dealQuality = 'fair'
  }
  
  // Rating Analysis
  const ratings = similarProducts.map(p => p.rating).filter((r): r is number => typeof r === 'number' && r > 0)
  const avgRating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 4.0
  const ratingVsAvg = (product.rating || 0) - avgRating
  
  // Capacity Analysis
  const capacities = similarProducts.map(p => p.capacity_cuft || p.capacity).filter((c): c is number => typeof c === 'number' && c > 0)
  const avgCapacity = capacities.length ? capacities.reduce((a, b) => a + b, 0) / capacities.length : capacity
  const capacityVsAvg = capacity - avgCapacity
  
  // Feature Detection
  const hasSmartFeatures = specs.smart_features || specs.wifi_enabled || specs.app_control || false
  const hasIceMaker = specs.ice_maker || specs.has_ice_maker || false
  const isEnergyStarCertified = specs.energy_star || false
  const isCounterDepth = product.type?.includes('counter-depth') || specs.installation === 'counter-depth' || false
  const doorConfig = specs.number_of_doors || null
  
  // Special features
  const specialFeatures = []
  if (hasSmartFeatures) specialFeatures.push('WiFi & Smart Home Integration')
  if (specs.dual_cooling) specialFeatures.push('Dual Cooling System')
  if (specs.water_dispenser) specialFeatures.push('Water Dispenser')
  if (specs.ice_maker) specialFeatures.push('Built-in Ice Maker')
  if (isEnergyStarCertified) specialFeatures.push('Energy Star Certified')
  if (specs.adjustable_shelves) specialFeatures.push('Adjustable Shelving')
  if (inferred.fingerprint) specialFeatures.push('Fingerprint Resistant')
  if (inferred.doorInDoor) specialFeatures.push('Door-in-Door Access')
  if (inferred.convertible) specialFeatures.push('Convertible Flex Zone')
  
  // Niche features
  if (inferred.dualZone) specialFeatures.push('Dual Zone Temperature')
  if (inferred.builtIn) specialFeatures.push('Built-In / Undercounter Capable')
  if (inferred.lock) specialFeatures.push('Security Lock')
  if (inferred.commercial) specialFeatures.push('Commercial Grade')
  if (inferred.glassDoor) specialFeatures.push('Glass Display Door')
  if (inferred.taps) specialFeatures.push(`${inferred.taps}-Tap System`)
  
  // Sizing Intelligence
  const width = product.width_in || product.dimensions?.width || 36
  const height = product.height_in || product.dimensions?.height || 70
  const depth = product.depth_in || product.dimensions?.depth || 30
  
  const fitsStandardSpace = width <= 36
  const requiresCustomSpace = width > 36
  const capacityPerSquareInch = (capacity * 1728) / (width * height * depth) // cubic feet to cubic inches
  const spaceEfficiencyScore = Math.min(capacityPerSquareInch / 0.5, 1) // Normalized to 0-1
  
  // Unique Selling Points
  const usps = []
  if (vsCategory < -15) usps.push(`${Math.abs(Math.round(vsCategory))}% below category average price`)
  if (ratingVsAvg > 0.3) usps.push(`Rated ${product.rating?.toFixed(1)}★ (above category average)`)
  if (capacityVsAvg > 3) usps.push(`${Math.round(capacityVsAvg)} cu ft more capacity than average`)
  if (isEnergyStarCertified) usps.push('Energy Star certified for lower bills')
  if (hasSmartFeatures) usps.push('Smart home integration')
  if (spaceEfficiencyScore > 0.8) usps.push('Excellent space efficiency')
  
  // Target Customer
  let targetCustomer = 'Ideal for most households'
  if (capacity >= 28) targetCustomer = 'Best for large families'
  else if (capacity <= 12) targetCustomer = 'Perfect for apartments or small spaces'
  else if (priceTier === 'premium' || priceTier === 'luxury') targetCustomer = 'Premium choice for design-focused buyers'
  else if (priceTier === 'budget') targetCustomer = 'Great value for budget-conscious shoppers'
  
  // Review Sentiment
  const rating = product.rating || product.specs?.rating || 0
  const reviews = product.reviews || product.specs?.reviews || 0
  let overallSentiment: 'excellent' | 'good' | 'mixed' | 'poor' = 'mixed'
  if (rating >= 4.5 && reviews > 100) overallSentiment = 'excellent'
  else if (rating >= 4.0 && reviews > 50) overallSentiment = 'good'
  else if (rating < 3.5) overallSentiment = 'poor'
  
  const trustScore = Math.round(
    (rating / 5) * 40 + // 40% weight on rating
    Math.min(reviews / 1000, 1) * 30 + // 30% weight on review count
    (isPriceDrop ? 15 : 0) + // 15% bonus for deals
    (isEnergyStarCertified ? 10 : 0) + // 10% bonus for certification
    (usps.length * 5) // 5% per USP
  )
  
  const isHighlyRated = rating >= 4.5 && reviews > 100
  const socialProof = reviews > 500 ? `Trusted by ${reviews.toLocaleString()}+ customers` : 
                      reviews > 100 ? `Popular choice with ${reviews} verified reviews` :
                      reviews > 0 ? `${reviews} customer reviews` : ''
  
  // Overall Score
  const score = Math.round(
    (5 - Math.abs(vsCategory / 20)) * 20 + // Value score (0-20)
    (rating / 5) * 30 + // Rating score (0-30)
    (specialFeatures.length / 6) * 25 + // Feature score (0-25)
    spaceEfficiencyScore * 15 + // Efficiency score (0-15)
    (isEnergyStarCertified ? 10 : 0) // Certification bonus (0-10)
  )
  
  // Ensure minimum score of 65 for any real product - "0" looks bad in UI
  // Products with ratings get boosted, energy star gets bonus
  const minScore = 65
  const finalScore = Math.max(score, minScore)
  
  return {
    score: Math.min(Math.max(finalScore, 0), 100),
    price: {
      current: product.price,
      historical30d: product.average_price_30d_cents ? product.average_price_30d_cents / 100 : null,
      allTimeLowest: product.lowest_price_ever_cents ? product.lowest_price_ever_cents / 100 : null,
      vsCategory: Math.round(vsCategory * 10) / 10,
      savingsOpportunity,
      isPriceDrop,
      dealQuality
    },
    competitive: {
      priceTier,
      ratingVsAvg: Math.round(ratingVsAvg * 10) / 10,
      capacityVsAvg: Math.round(capacityVsAvg * 10) / 10,
      uniqueSellingPoints: usps,
      targetCustomer
    },
    features: {
      hasSmartFeatures,
      hasIceMaker,
      isEnergyStarCertified,
      isCounterDepth,
      doorConfig,
      doorInDoor: inferred.doorInDoor,
      specialFeatures
    },
    sizing: {
      fitsStandardSpace,
      requiresCustomSpace,
      capacityPerSquareInch,
      spaceEfficiencyScore: Math.round(spaceEfficiencyScore * 100) / 100
    },
    reviews: {
      overallSentiment,
      trustScore: Math.min(trustScore, 100),
      isHighlyRated,
      socialProof
    }
  }
}

export function getScoreLabel(score: number): string {
  if (score >= 90) return 'Excellent!'
  if (score >= 80) return 'Great'
  if (score >= 70) return 'Good'
  if (score >= 60) return 'Fair'
  return 'Average'
}

export function getScoreColor(score: number): string {
  if (score >= 90) return 'bg-green-100 text-green-800'
  if (score >= 80) return 'bg-blue-100 text-blue-800'
  if (score >= 70) return 'bg-yellow-100 text-yellow-800'
  return 'bg-gray-100 text-gray-800'
}

